import * as React from 'react';
import { Badge } from '@seller-hermes/features/ui-v2/Badge';
import Input from '@seller-hermes/features/ui-v2/Input';
import { Kbd } from '@seller-hermes/features/ui-v2/Kbd';
import { cn } from '@seller-hermes/utils/cn';
import {
  DEV_ROUTE_MAX_LENGTH,
  DevRouteLastResponse,
  getDevRouteSnapshot,
  normalizeDevRouteName,
  setDevRoute,
  subscribeDevRoute,
} from '@seller-hermes/utils/dev-route';

const lastResponseText = (last: DevRouteLastResponse | null) => {
  if (!last) return 'nenhuma chamada ainda';
  if (last.kind === 'laptop')
    return `última chamada veio do laptop de ${last.dev}`;
  if (last.kind === 'blocked') return 'última chamada barrada (CORS ou rede)';
  return 'última chamada veio do sandbox';
};

// Alt+Shift+D (Option+Shift+D no Mac). `code` e não `key`: no Mac o Option
// troca o caractere digitado.
const isToggleShortcut = (e: KeyboardEvent) =>
  e.code === 'KeyD' && e.altKey && e.shiftKey && !e.ctrlKey && !e.metaKey;

/**
 * Selo fixo da rota de dev. Só monta fora de prod (`snapshot.available` vem
 * de `installDevRoute`, que em prod não roda). Sem rota ativa, fica invisível
 * até o atalho abrir o campo.
 */
export const DevRouteBadge: React.FC = () => {
  const { available, route, last } = React.useSyncExternalStore(
    subscribeDevRoute,
    getDevRouteSnapshot,
  );
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');

  React.useEffect(() => {
    if (!available) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isToggleShortcut(e)) return;
      e.preventDefault();
      setEditing((v) => !v);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [available]);

  React.useEffect(() => {
    if (editing) setDraft(route ?? '');
  }, [editing, route]);

  if (!available || (!route && !editing)) return null;

  const invalid = draft.trim() !== '' && !normalizeDevRouteName(draft);

  const save = () => {
    if (invalid) return;
    setDevRoute(draft);
    setEditing(false);
  };

  return (
    <div
      data-testid="dev-route-badge"
      className="bg-background/95 fixed bottom-14 right-4 z-[60] flex flex-col gap-1 rounded-md border px-2 py-1 text-xs shadow-md"
    >
      {editing && (
        <form
          className="flex flex-col gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <Input
            autoFocus
            aria-label="Nome do dev"
            placeholder="nome do dev (vazio desliga)"
            value={draft}
            maxLength={DEV_ROUTE_MAX_LENGTH}
            error={invalid}
            className="h-8 text-xs"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setEditing(false);
            }}
          />
          <span
            className={cn(
              'text-muted-foreground',
              invalid && 'text-destructive',
            )}
          >
            {invalid ? (
              `use a-z, 0-9 e hífen, até ${DEV_ROUTE_MAX_LENGTH}`
            ) : (
              <>
                Enter salva, Esc fecha, <Kbd>Alt+Shift+D</Kbd> alterna
              </>
            )}
          </span>
        </form>
      )}
      {route && (
        <div className="flex items-center gap-2 whitespace-nowrap">
          <Badge
            variant="outline"
            className="cursor-pointer border-amber-400 text-amber-600"
            title="Alt+Shift+D pra trocar"
            onClick={() => setEditing(true)}
          >
            rota dev: {route}
          </Badge>
          <span
            data-testid="dev-route-last"
            className={cn(
              'text-muted-foreground',
              last?.kind === 'laptop' && 'text-green-600',
              last?.kind === 'blocked' && 'text-destructive',
            )}
          >
            {lastResponseText(last)}
          </span>
          <button
            type="button"
            aria-label="Desligar rota dev"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => setDevRoute(null)}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default DevRouteBadge;
